<template>
  <div class="root-box">
    <div class="toolbar">
      <div class="areas">
        <span v-for="area in areaTabs" :key="area.id" class="area-tab"
          :class="{ active: area.id === currentAreaId, pulse: area.id === locatedAreaId }"
          @click="selectArea(area.id)">
          <span class="area-name">{{ area.name }}</span>
          <span class="area-cars">{{ area.cars }}车</span>
          <i class="el-icon-plus area-add" title="添加" @click.stop="add(area.id)"></i>
        </span>
      </div>
      <div class="btn-box">
        <el-input v-model="driverNo" class="driver-locate" size="small" clearable placeholder="司机编号"
          @keyup.enter.native="locateDriver" @clear="clearLocate">
          <el-button slot="append" @click="locateDriver">查询</el-button>
        </el-input>
        <el-select v-model="pathId" size="small" clearable filterable placeholder="发班路线" style="width: 180px"
          @change="onPathChange">
          <el-option v-for="path in areaPaths" :key="path.id" :label="path.name" :value="path.id"></el-option>
        </el-select>
        <el-button type="primary" size="small" plain @click="query">刷新</el-button>
      </div>
    </div>

    <div class="box" ref="box">
      <driver-list ref="list" :key="currentAreaId" :data="currentList" :areaId="currentAreaId"
        :highlightId="highlightId" :onSchedulingChange="query" />
    </div>
  </div>
</template>

<script>
import DriverList from "./DriverList";
import { getSchedulingMap, listSuitableDrivers } from "@/api";
import { mapState } from "vuex";

export default {
  components: {
    DriverList
  },

  created() {
    this.query();
  },

  data() {
    return {
      schedulingMap: {},
      currentAreaId: null,
      pathId: null,
      filtered: false,
      filteredList: [],
      driverNo: "",
      highlightId: null,
      locatedAreaId: null,
      skipScrollReset: false,
      locateTimer: null
    };
  },

  computed: {
    ...mapState({
      areaOpts: (state) => state.areaOpts,
      pathList: (state) => state.pathList
    }),
    areaPaths() {
      return (this.pathList || []).filter((path) => path.startArea === this.currentAreaId && path.state !== false);
    },
    areaTabs() {
      return Object.keys(this.schedulingMap || {}).map((id) => {
        const areaId = parseInt(id);
        const list = this.schedulingMap[id] || [];
        const area = (this.areaOpts || []).find((item) => item.id === areaId);
        return {
          id: areaId,
          name: area ? area.name : id,
          cars: list.length,
          list
        };
      });
    },
    currentList() {
      if (this.filtered) {
        return this.filteredList;
      }
      const current = this.areaTabs.find((item) => item.id === this.currentAreaId);
      return current ? current.list : [];
    }
  },

  watch: {
    currentAreaId() {
      this.pathId = null;
      this.restore();
      if (this.skipScrollReset) {
        return;
      }
      this.$nextTick(() => {
        if (this.$refs.box) {
          this.$refs.box.scrollTop = 0;
        }
      });
    }
  },

  beforeDestroy() {
    clearTimeout(this.locateTimer);
  },

  methods: {
    async query() {
      const resp = await getSchedulingMap();
      this.schedulingMap = resp.data || {};
      const ids = this.areaTabs.map((item) => item.id);
      if (!ids.includes(this.currentAreaId)) {
        this.currentAreaId = ids.length ? ids[0] : null;
      }
      if (this.filtered && this.pathId) {
        await this.filterDepart();
      }
    },

    onPathChange() {
      if (!this.pathId) {
        this.restore();
        return;
      }
      this.filterDepart();
    },

    async filterDepart() {
      if (!this.pathId) {
        return;
      }
      const resp = await listSuitableDrivers(this.pathId);
      this.filteredList = resp.data || [];
      this.filtered = true;
      this.$nextTick(() => {
        if (this.$refs.box) {
          this.$refs.box.scrollTop = 0;
        }
      });
    },

    restore() {
      this.filtered = false;
      this.filteredList = [];
    },

    selectArea(areaId) {
      if (areaId === this.currentAreaId) {
        return;
      }
      this.clearLocate();
      this.currentAreaId = areaId;
    },

    clearLocate() {
      this.highlightId = null;
      this.locatedAreaId = null;
    },

    locateDriver() {
      const no = (this.driverNo || "").trim();
      if (!no) {
        this.$message.warning("请输入司机编号");
        return;
      }
      let hit = null;
      Object.keys(this.schedulingMap || {}).some((id) => {
        const driver = (this.schedulingMap[id] || []).find((item) => String(item.driverNo || "").trim() === no);
        if (!driver) {
          return false;
        }
        hit = { areaId: parseInt(id), driver };
        return true;
      });
      if (!hit) {
        this.clearLocate();
        this.$message.warning("未找到编号为 " + no + " 的司机");
        return;
      }
      const area = this.areaTabs.find((item) => item.id === hit.areaId);
      this.pathId = null;
      this.restore();
      this.skipScrollReset = true;
      this.highlightId = null;
      this.locatedAreaId = null;
      if (this.currentAreaId !== hit.areaId) {
        this.currentAreaId = hit.areaId;
      }
      this.$nextTick(() => {
        this.skipScrollReset = false;
        this.locatedAreaId = hit.areaId;
        this.scrollToDriver(hit.driver.id);
        clearTimeout(this.locateTimer);
        this.locateTimer = setTimeout(() => {
          this.highlightId = hit.driver.id;
        }, 280);
      });
      this.$message.success("已定位到" + (area ? area.name + " " : "") + hit.driver.driverNo);
    },

    scrollToDriver(id) {
      const list = this.$refs.list && this.$refs.list.$el;
      const box = this.$refs.box;
      if (!list || !box) {
        return;
      }
      const row = list.querySelector('[data-driver-id="' + id + '"]');
      if (!row) {
        return;
      }
      const top = box.scrollTop + (row.getBoundingClientRect().top - box.getBoundingClientRect().top) - (box.clientHeight - row.offsetHeight) / 2;
      if (box.scrollTo) {
        box.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        return;
      }
      box.scrollTop = Math.max(0, top);
    },

    add(areaId) {
      const open = () => {
        if (this.$refs.list) {
          this.$refs.list.add();
        }
      };
      if (this.currentAreaId !== areaId) {
        this.currentAreaId = areaId;
        this.$nextTick(open);
        return;
      }
      open();
    }
  }
};
</script>

<style scoped lang="less">
.root-box {
  display: flex;
  flex-direction: column;

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    gap: 12px;
  }

  .areas {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .area-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 6px 5px 14px;
    border-radius: 18px;
    background: #f0f2f5;
    color: #434343;
    cursor: pointer;
    line-height: 22px;
    user-select: none;

    .area-name {
      font-size: 14px;
    }

    .area-cars {
      min-width: 42px;
      padding: 0 8px;
      line-height: 22px;
      border-radius: 11px;
      background: #fff;
      color: #8c8c8c;
      font-size: 12px;
      text-align: center;
    }

    .area-add {
      width: 18px;
      height: 18px;
      line-height: 18px;
      border-radius: 9px;
      background: #fff;
      color: #8c8c8c;
      font-size: 12px;
      text-align: center;

      &:hover {
        background: #4f94d8;
        color: #fff;
      }
    }

    &:hover {
      background: #f5faff;
      color: #4f94d8;
    }

    &.pulse {
      animation: area-locate 0.8s ease;
    }

    &.active {
      background: #e6f4ff;
      color: #4f94d8;

      .area-cars {
        background: #d6ebff;
        color: #4f94d8;
      }

      .area-add {
        background: #fff;
        color: #4f94d8;
      }
    }
  }

  .btn-box {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 8px;
  }

  .driver-locate {
    width: 210px;

    /deep/ .el-input-group__append {
      padding: 0;
      background: #4f94d8;
      border-color: #4f94d8;

      .el-button {
        margin: 0;
        padding: 0 14px;
        color: #fff;
        background: transparent;
        border: none;
      }
    }
  }

  .box {
    overflow-x: auto;
    overflow-y: scroll;
    height: 750px;
  }
}

@keyframes area-locate {
  0% {
    box-shadow: 0 0 0 0 rgba(79, 148, 216, 0.55);
  }

  100% {
    box-shadow: 0 0 0 8px rgba(79, 148, 216, 0);
  }
}
</style>
